import { BaseClient } from "../BaseClient";
import { getMimeType } from "../Common";
import { Empty } from "../compiled-protos/common";
import { type AdvancePermitStageRequest, type Inspection, type Permit, type PermitActivity, type PermitActivityType, type PermitBlockerRequest, PermitDocument, type PermitGroup, PermitGroupDocument, type PermitHandoffRecipient, type PermitListItem, type PermitListMergeRequest, type PermitListReorderRequest, type PermitNoteRequest, type PermitTrade } from "../compiled-protos/permit";
import { PermitServiceClient } from "../compiled-protos/permit.client";
import { File, FileClient } from "../File";
import { S3Client, URLObject } from "../S3File";

const PERMIT_CALL_TIMEOUT_MS = 30_000;

export class PermitClient extends BaseClient {
  self: PermitServiceClient;
  private S3: S3Client;
  private FileClient: FileClient;
  private permitsS3BucketName = 'kalos-permits'

  constructor(host: string, userID?: number) {
    super(host, userID);
    this.self = new PermitServiceClient(this.transport);
    this.S3 = new S3Client(host, userID);
    this.FileClient = new FileClient(host, userID);
  }

  /**
   * Every Permitting call gives up after 30 seconds (DEADLINE_EXCEEDED), so a screen shows
   * its error with Retry instead of loading forever.
   */
  protected override getMetaData(authToken?: string): { meta: Record<string, string | string[]>; timeout: number } {
    return { ...super.getMetaData(authToken), timeout: PERMIT_CALL_TIMEOUT_MS };
  }

  /*
   * PermitGroup
   */
  public async BatchGetPermitGroup(input: PermitGroup) {
    const res = await this.self.batchGetPermitGroup(input, this.getMetaDataWithoutCache()).response;
    return res
  }
  public async GetPermitGroup(input: PermitGroup) {
    const res = await this.self.getPermitGroup(input, this.getMetaDataWithoutCache()).response;
    return res
  }

  public async CreatePermitGroup(input: PermitGroup) {
    const res = await this.self.createPermitGroup(input, this.getMetaData()).response;
    return res
  }

  public async UpdatePermitGroup(input: PermitGroup) {
    const res = await this.self.updatePermitGroup(input, this.getMetaData()).response;
    return res
  }

  public async DeletePermitGroup(input: PermitGroup) {
    const res = await this.self.deletePermitGroup(input, this.getMetaData()).response;
    return res
  }

  /**
   * PermitGroupDocument
   */
  public async _createPermitGroupDocument(input: PermitGroupDocument) {
    const res = await this.self.createPermitGroupDocument(input, this.getMetaData()).response
    return res
  }

  public async CreatePermitGroupDocument({ permitGroupId, fileName, fileData }: { permitGroupId: number, fileName: string, fileData: Uint8Array }) {
    const contentType = getMimeType(fileName);
    const fileKey = `permit-group-${permitGroupId}-${Date.now()}-${fileName}`;
    const urlObj = URLObject.create({
      bucket: this.permitsS3BucketName,
      key: fileKey,
      contentType: contentType,
    });
    const urlRes = await this.S3.GetUploadURL(urlObj);

    if (!urlRes) {
      throw new Error('Failed to get upload URL');
    }

    const uploadRes = await fetch(urlRes.url, {
      body: fileData,
      method: 'PUT',
    });

    if (uploadRes.status !== 200) {
      throw new Error('Failed to upload file');
    }

    const fileObj = File.create({
      bucket: this.permitsS3BucketName,
      name: fileKey,
      mimeType: contentType,
    });
    const fileRes = await this.FileClient.Create(fileObj);

    const req = PermitGroupDocument.create({
      fileId: fileRes.id,
      permitGroupId: permitGroupId,
    });

    const docResult = await this._createPermitGroupDocument(req);
    return docResult;
  }

  public async DeletePermitGroupDocument(input: PermitGroupDocument) {
    const res = await this.self.deletePermitGroupDocument(input, this.getMetaData()).response;
    return res
  }

  public async GetDocumentDownloadURL({ filename, mimeType }: { filename: string, mimeType: string }) {
    const res = await this.S3.GetDownloadURL(URLObject.create({
      bucket: this.permitsS3BucketName,
      key: filename,
      contentType: mimeType,
    }));
    return res;
  }


  /*
   * Permit
   */
  public async BatchGetPermit(input: Permit) {
    const res = await this.self.batchGetPermit(input, this.getMetaDataWithoutCache()).response;
    return res
  }
  public async GetPermit(input: Permit) {
    const res = await this.self.getPermit(input, this.getMetaDataWithoutCache()).response;
    return res
  }

  public async CreatePermit(input: Permit) {
    const res = await this.self.createPermit(input, this.getMetaData()).response;
    return res
  }

  public async UpdatePermit(input: Permit) {
    const res = await this.self.updatePermit(input, this.getMetaData()).response;
    return res
  }

  public async DeletePermit(input: Permit) {
    const res = await this.self.deletePermit(input, this.getMetaData()).response;
    return res
  }
  /**
   * PermitDocument
   */
  public async _createPermitDocument(input: PermitDocument) {
    const res = await this.self.createPermitDocument(input, this.getMetaData()).response
    return res
  }

  public async CreatePermitDocument({ permitId, fileName, fileData, documentTypeId = 0 }: { permitId: number, fileName: string, fileData: Uint8Array, documentTypeId?: number }) {
    const contentType = getMimeType(fileName);
    const fileKey = `permit-${permitId}-${Date.now()}-${fileName}`;
    const urlObj = URLObject.create({
      bucket: this.permitsS3BucketName,
      key: fileKey,
      contentType: contentType,
    });
    const urlRes = await this.S3.GetUploadURL(urlObj);

    if (!urlRes) {
      throw new Error('Failed to get upload URL');
    }

    const uploadRes = await fetch(urlRes.url, {
      body: fileData,
      method: 'PUT',
    });

    if (uploadRes.status !== 200) {
      throw new Error('Failed to upload file');
    }

    const fileObj = File.create({
      bucket: this.permitsS3BucketName,
      name: fileKey,
      mimeType: contentType,
    });
    const fileRes = await this.FileClient.Create(fileObj);

    const req = PermitDocument.create({
      fileId: fileRes.id,
      permitId: permitId,
      documentTypeId,
    });

    const docResult = await this._createPermitDocument(req);
    return docResult;
  }

  public async UpdatePermitDocument(input: PermitDocument) {
    const res = await this.self.updatePermitDocument(input, this.getMetaData()).response;
    return res
  }

  public async DeletePermitDocument(input: PermitDocument) {
    const res = await this.self.deletePermitDocument(input, this.getMetaData()).response;
    return res
  }

  /*
  * PermitStatus
  */
  public async BatchGetPermitStatuses() {
    const res = await this.self.batchGetPermitStatus({}, this.getMetaDataWithoutCache()).response;
    return res
  }

  /*
  * PermitTrade
  */
  public async BatchGetPermitTrades(input: PermitTrade) {
    const res = await this.self.batchGetPermitTrade(input, this.getMetaDataWithoutCache()).response;
    return res
  }

  /*
  * Inspection
  */
  public async BatchGetInspection(input: Inspection) {
    const res = await this.self.batchGetInspection(input, this.getMetaDataWithoutCache()).response;
    return res
  }
  public async CreateInspection(input: Inspection) {
    const res = await this.self.createInspection(input, this.getMetaData()).response;
    return res
  }
  public async UpdateInspection(input: Inspection) {
    const res = await this.self.updateInspection(input, this.getMetaData()).response;
    return res
  }
  public async DeleteInspection(input: Inspection) {
    const res = await this.self.deleteInspection(input, this.getMetaData()).response;
    return res
  }
  /*
  * InspectionStatus
  */
  public async BatchGetInspectionStatus() {
    const res = await this.self.batchGetInspectionStatus(Empty.create(), this.getMetaDataWithoutCache()).response;
    return res
  }


  /*
   * Permitting lookup lists: blockers, next actions, jurisdictions, inspection types,
   * paths, methods and document types. Reading is open; changes need the PermitAdmin group.
   * 25 per page; pickers and admin lists set withoutLimit.
   */
  public async BatchGetPermitListItem(input: PermitListItem) {
    return await this.self.batchGetPermitListItem(input, this.getMetaDataWithoutCache()).response;
  }
  public async CreatePermitListItem(input: PermitListItem) {
    return await this.self.createPermitListItem(input, this.getMetaData()).response;
  }
  public async RenamePermitListItem(input: PermitListItem) {
    return await this.self.renamePermitListItem(input, this.getMetaData()).response;
  }
  public async ReorderPermitListItems(input: PermitListReorderRequest) {
    return await this.self.reorderPermitListItems(input, this.getMetaData()).response;
  }
  public async RetirePermitListItem(input: PermitListItem) {
    return await this.self.retirePermitListItem(input, this.getMetaData()).response;
  }
  public async RestorePermitListItem(input: PermitListItem) {
    return await this.self.restorePermitListItem(input, this.getMetaData()).response;
  }
  public async MergePermitListItem(input: PermitListMergeRequest) {
    return await this.self.mergePermitListItem(input, this.getMetaData()).response;
  }

  /*
   * Blockers on a permit.
   */
  public async AddPermitBlocker(input: PermitBlockerRequest) {
    return await this.self.addPermitBlocker(input, this.getMetaData()).response;
  }
  public async RemovePermitBlocker(input: PermitBlockerRequest) {
    return await this.self.removePermitBlocker(input, this.getMetaData()).response;
  }
  /**
   * Handoff recipients (Settings > Handoffs). PermitAdmin only, reads included.
   */
  public async BatchGetPermitHandoffRecipient(input: PermitHandoffRecipient) {
    return await this.self.batchGetPermitHandoffRecipient(input, this.getMetaData()).response;
  }
  public async CreatePermitHandoffRecipient(input: PermitHandoffRecipient) {
    return await this.self.createPermitHandoffRecipient(input, this.getMetaData()).response;
  }
  public async UpdatePermitHandoffRecipient(input: PermitHandoffRecipient) {
    return await this.self.updatePermitHandoffRecipient(input, this.getMetaData()).response;
  }
  public async RetirePermitHandoffRecipient(input: PermitHandoffRecipient) {
    return await this.self.retirePermitHandoffRecipient(input, this.getMetaData()).response;
  }
  public async RestorePermitHandoffRecipient(input: PermitHandoffRecipient) {
    return await this.self.restorePermitHandoffRecipient(input, this.getMetaData()).response;
  }
  /**
   * Re-sends a permit's handoff email to its current recipients (PermitAdmin only). Send
   * {id} of the handoff activity. Resolves the new activity entry.
   */
  public async RetryPermitHandoff(input: PermitActivity) {
    return await this.self.retryPermitHandoff(input, this.getMetaData()).response;
  }

  /**
   * The only way a permit changes stage: writes the facts in fieldMask, resolves blockers,
   * moves it to targetStatus and sends (or skips) the issuance handoff. A gate that isn't
   * met is FailedPrecondition with a message to show as it is.
   */
  public async AdvancePermitStage(input: AdvancePermitStageRequest) {
    return await this.self.advancePermitStage(input, this.getMetaData()).response;
  }

  /**
   * Activity for exactly one of a permit request, permit or inspection id, or a list kind.
   * Newest first, 25 per page.
   */
  public async BatchGetPermitActivity(input: PermitActivity) {
    return await this.self.batchGetPermitActivity(input, this.getMetaDataWithoutCache()).response;
  }

  /** Activity type names, looked up by activityTypeId. */
  public async BatchGetPermitActivityType(input: PermitActivityType) {
    return await this.self.batchGetPermitActivityType(input, this.getMetaDataWithoutCache()).response;
  }

  /**
   * Adds a note to a permit request or permit. The server sets the author and the time and
   * returns the activity entry with the author's name.
   */
  public async CreatePermitNote(input: PermitNoteRequest) {
    return await this.self.createPermitNote(input, this.getMetaData()).response;
  }
}
